import { Activity, Building2, CalendarCheck, CalendarDays, FileText, Globe, KanbanSquare, LayoutDashboard, Newspaper, Package, Palette, PenSquare, Repeat, ScrollText, Send, Share2, ShieldCheck, Stethoscope, UserCog, Users, Wallet } from "lucide-react";

import { can, type StaffAccess, type StaffPermission } from "./access";

/**
 * The admin menu. Each page lists the permission its data needs, as the
 * server checks it; staff only see what they can open (ADR 0022).
 * `assigned`: a role limited to assigned clients is enough.
 */
export const NAV: { to: string; label: string; icon: typeof Activity; need: StaffPermission | StaffPermission[]; assigned?: boolean }[] = [
  { to: "/", label: "Overview", icon: LayoutDashboard, need: "platform.orgs.read" },
  { to: "/clients", label: "Clients", icon: Stethoscope, need: "platform.orgs.read", assigned: true },
  { to: "/content", label: "Client content", icon: PenSquare, need: "platform.clients.manage", assigned: true },
  { to: "/social", label: "Social health", icon: Share2, need: ["platform.clients.manage", "platform.integrations.manage"], assigned: true },
  { to: "/verification", label: "Registration checks", icon: ShieldCheck, need: "platform.verification.manage" },
  { to: "/organizations", label: "Organizations", icon: Building2, need: "platform.orgs.read" },
  { to: "/packages", label: "Packages", icon: Package, need: "platform.catalog.manage" },
  { to: "/subscriptions", label: "Subscriptions", icon: Repeat, need: "platform.catalog.manage" },
  { to: "/payments", label: "Payments", icon: Wallet, need: "platform.payments.manage" },
  { to: "/pipeline", label: "Production pipeline", icon: KanbanSquare, need: "platform.deliverables.manage" },
  { to: "/schedule", label: "Shoot schedule", icon: CalendarDays, need: "platform.deliverables.manage" },
  { to: "/bookings", label: "Live bookings", icon: CalendarCheck, need: "platform.bookings.manage" },
  { to: "/publishing", label: "Publishing log", icon: Send, need: "platform.deliverables.manage" },
  { to: "/websites", label: "Doctor websites", icon: Globe, need: "platform.websites.manage" },
  { to: "/templates", label: "Website templates", icon: Palette, need: "platform.websites.manage" },
  { to: "/articles", label: "Articles", icon: Newspaper, need: "platform.articles.manage" },
  { to: "/cms", label: "Site content", icon: FileText, need: "platform.cms.manage" },
  { to: "/staff", label: "Staff and roles", icon: UserCog, need: ["platform.users.manage", "platform.roles.manage"] },
  { to: "/users", label: "Users", icon: Users, need: "platform.users.manage" },
  { to: "/audit", label: "Audit log", icon: ScrollText, need: "platform.audit.read" },
  { to: "/platform", label: "Platform health", icon: Activity, need: "platform.orgs.read" },
];

type Rule = { to: string; need: StaffPermission | StaffPermission[]; assigned?: boolean };

/** Pages outside the menu. A client's own pages check the rest themselves. */
const EXTRA: Rule[] = [
  { to: "/clients/new", need: "platform.users.manage" },
  { to: "/clients/", need: "platform.orgs.read", assigned: true },
];

function allowed(access: StaffAccess | undefined, r: Rule): boolean {
  return (Array.isArray(r.need) ? r.need : [r.need]).some((p) => can(access, p, { assigned: r.assigned === true }));
}

/** The menu entries this staff member can open. */
export function visibleNav(access: StaffAccess | undefined): typeof NAV {
  return NAV.filter((n) => allowed(access, n));
}

/** The most specific rule for a path: `/` only matches itself, others their subpaths too. */
function ruleFor(pathname: string): Rule | undefined {
  const matches = (to: string) => (to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(to.endsWith("/") ? to : `${to}/`));
  return [...EXTRA, ...NAV].filter((r) => matches(r.to)).sort((a, b) => b.to.length - a.to.length)[0];
}

/**
 * Whether a page would load for this staff member. Pages they can't use
 * aren't rendered at all, so they never fire requests the server refuses.
 */
export function canOpen(access: StaffAccess | undefined, pathname: string): boolean {
  const r = ruleFor(pathname);
  return r === undefined || allowed(access, r);
}

/** Where to land after signing in: the first page in the menu they can open. */
export function homeFor(access: StaffAccess | undefined): string | null {
  return visibleNav(access)[0]?.to ?? null;
}
